import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import {
  buildMeasureResult,
  computeAreaBoundaryIndices,
  computeAreaPlaneProjection,
  type MeasurePoint,
  type MeasureResult,
  type MeasureType,
  resolveAreaBoundaryPoints,
} from '@/plugins/tools/measure/MeasureData'
import {
  createTextSprite,
  disposeTextSprite,
  getTextSpriteMetrics,
  type TextSpriteOptions,
  updateTextSpriteScaleForCamera,
} from '@/plugins/tools/shared/TextSprite'
import { createFillMaterial, createPointMaterial } from '@/plugins/tools/shared/ToolMaterials'
import {
  getActiveViewId,
  getOptionalViewRegistry,
  getViewToolsRoot,
} from '@/plugins/tools/shared/viewScope'
import {
  createWebGLWideLineFactory,
  createWideLineFactory,
  type WideLineFactory,
  type WideLineMaterial,
  type WideLineObject,
  type WideLineStyle,
} from '@/plugins/tools/shared/wideLine'
import type { EngineContext } from '@/runtime/EngineContext'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { Plugin } from '@/shared/types/plugin'
import type {
  MeasureService,
  RenderContributor,
  RenderPipeline,
  ViewEventRouter,
  ViewInputConsumer,
  ViewPointerEvent,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { formatArea, formatLength, type UnitPreference } from '@/shared/unitPresentation'

const CIRCLE_SEGMENTS = 64
const ARC_SEGMENTS = 32
const _MEASURE_LABEL_SCREEN_PX = 82
const COORDINATE_LABEL_SCREEN_PX = 70
const COORDINATE_LABEL_OFFSET_PX = 8
const POINT_LABEL_OFFSET_PX = -34
const POINT_LABEL_SCREEN_PX = 84
const MEASURE_LINE_COLOR = 0xff0000
const MEASURE_LINE_WIDTH_PX = 1.5
const MEASURE_LINE_DASH_PX = 12
const MEASURE_LINE_GAP_PX = 12
const MEASURE_AREA_FILL_COLOR = 0xff0000
const MEASURE_AREA_FILL_OPACITY = 0.35
const POINT_MARKER_SIZE = 8
const _HOVER_POINT_SIZE = 10
const HOVER_HIGHLIGHT_SCREEN_PX = 24
const MEASURE_LOCAL_ORIGIN_THRESHOLD = 10000
const REMOVE_ACTION_HIT_PADDING_PX = 4
const REMOVE_DOUBLE_CLICK_GUARD_MS = 500
const MEASURE_LABEL_OPTIONS: TextSpriteOptions = {
  fontFace: 'Arial',
  fontSize: 24,
  fontWeight: 900,
  textColor: '#ffffff',
  backgroundColor: '#00000099',
  padding: 10,
  borderRadius: 6,
  borderThickness: 1,
  borderColor: '#ffffff59',
  textStrokeWidth: 0.75,
  textStrokeColor: '#00000080',
}
const POINT_LABEL_OPTIONS: TextSpriteOptions = {
  fontFace: 'Arial',
  fontSize: 20,
  fontWeight: 700,
  textColor: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0)',
  padding: 10,
  borderRadius: 0,
  borderThickness: 0,
  borderColor: 'rgba(0, 0, 0, 0)',
  textStrokeWidth: 1,
  textStrokeColor: '#ff0000',
}
const COORDINATE_LABEL_OPTIONS: TextSpriteOptions = {
  ...MEASURE_LABEL_OPTIONS,
  fontSize: 22,
  fontWeight: 'bold',
  placement: 'below',
  textAlign: 'left',
}
const REMOVE_ACTION_OPTIONS = {
  glyph: '×',
  size: 18,
  inset: 3,
  backgroundColor: '#d41620',
  textColor: '#ffffff',
} as const

type CoordinatePointRemoveTarget =
  | { scope: 'draft'; pointId: string }
  | { scope: 'result'; resultId: string; pointId: string }

type RemoveHitTarget = {
  target: CoordinatePointRemoveTarget
  rect: { x: number; y: number; width: number; height: number }
}

type RecentRemoveActivation = {
  viewId: string
  rect: RemoveHitTarget['rect']
  timeStamp: number
}

type PreviewMeasureService = MeasureService & {
  getPreviewPoint?: () => MeasurePoint | null
  getPreviewViewId?: () => string | null
  getDraftViewId?: () => string | null
  getCoordinatePreviewLabel?: () => string | null | undefined
}

type AnchoredLabelUserData = {
  _labelAnchor?: THREE.Vector3
  _labelOffsetYPx?: number
  _labelTargetPx?: number
  _measureDecoration?: 'point-marker' | 'point-label'
  _measurePointRemoveTarget?: CoordinatePointRemoveTarget
}

type MeasureLineUserData = {
  _measureDashReference?: THREE.Vector3
}

function formatAngle(degrees: number): string {
  return `${degrees.toFixed(1)}°`
}

function formatDistance(meters: number, preference: UnitPreference): string {
  return formatLength(meters, preference).displayText
}

function formatCoordinateLabel(position: THREE.Vector3, preference: UnitPreference): string {
  return [
    `X/E ${formatDistance(position.x, preference)}`,
    `Y/N ${formatDistance(position.y, preference)}`,
    `Z/U ${formatDistance(position.z, preference)}`,
  ].join('\n')
}

function midpoint(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
  return new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5)
}

function resolveMeasureVisualOrigin(points: ReadonlyArray<MeasurePoint>): THREE.Vector3 {
  if (points.length === 0) {
    return new THREE.Vector3()
  }

  let maxAbs = 0
  const origin = new THREE.Vector3()
  for (const point of points) {
    const { position } = point
    maxAbs = Math.max(maxAbs, Math.abs(position.x), Math.abs(position.y), Math.abs(position.z))
    origin.add(position)
  }

  if (maxAbs < MEASURE_LOCAL_ORIGIN_THRESHOLD) {
    return new THREE.Vector3()
  }

  return origin.divideScalar(points.length)
}

function toLocalPosition(position: THREE.Vector3, origin: THREE.Vector3): THREE.Vector3 {
  return position.clone().sub(origin)
}

function createLocalMeasureResult(result: MeasureResult, origin: THREE.Vector3): MeasureResult {
  if (origin.lengthSq() === 0) {
    return result
  }

  return {
    ...result,
    points: result.points.map((point) => ({
      ...point,
      position: toLocalPosition(point.position, origin),
    })),
    ...(result.circle && {
      circle: {
        ...result.circle,
        center: toLocalPosition(result.circle.center, origin),
      },
    }),
  }
}

function createMeasureLabel(text: string): THREE.Sprite {
  return createTextSprite(text, MEASURE_LABEL_OPTIONS)
}

function createAnchoredLabel(
  text: string,
  anchor: THREE.Vector3,
  options: TextSpriteOptions,
  offsetYPx: number,
  targetPx: number
): THREE.Sprite {
  const label = createTextSprite(text, options)
  const userData = label.userData as AnchoredLabelUserData
  userData._labelAnchor = anchor.clone()
  userData._labelOffsetYPx = offsetYPx
  userData._labelTargetPx = targetPx
  label.position.copy(anchor)
  return label
}

function createPointLabel(
  index: number,
  position: THREE.Vector3,
  pointLabel?: string
): THREE.Sprite {
  const text = pointLabel?.trim() || `Pt${index + 1}`
  const label = createAnchoredLabel(
    text,
    position,
    POINT_LABEL_OPTIONS,
    POINT_LABEL_OFFSET_PX,
    POINT_LABEL_SCREEN_PX
  )
  ;(label.userData as AnchoredLabelUserData)._measureDecoration = 'point-label'
  return label
}

function getPointLabelNumber(pointLabel?: string): number | null {
  const match = pointLabel?.trim().match(/^Pt(\d+)$/i)
  if (!match) {
    return null
  }

  const value = Number(match[1])
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

function getMaxPointLabelNumber(points: ReadonlyArray<MeasurePoint>): number {
  return points.reduce((max, point) => {
    const pointNumber = getPointLabelNumber(point.label)
    return pointNumber ? Math.max(max, pointNumber) : max
  }, 0)
}

function getNextPreviewPointLabel(
  currentPoints: ReadonlyArray<MeasurePoint>,
  completedMaxPointNumber: number
): string {
  const draftMaxPointNumber = getMaxPointLabelNumber(currentPoints)
  const maxPointNumber = Math.max(completedMaxPointNumber, draftMaxPointNumber)
  if (maxPointNumber > 0) {
    return `Pt${maxPointNumber + 1}`
  }

  return `Pt${currentPoints.length + 1}`
}

function createHoverHighlightSprite(anchor: THREE.Vector3): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64

  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#00ff00'
    ctx.lineWidth = 4
    ctx.strokeRect(16, 16, 32, 32)
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true

  const material = new THREE.SpriteMaterial({
    map: texture,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  })
  const sprite = new THREE.Sprite(material)
  sprite.name = 'measure-hover-highlight'
  sprite.position.copy(anchor)

  const userData = sprite.userData as AnchoredLabelUserData & Record<string, unknown>
  userData._labelAnchor = anchor.clone()
  userData._labelOffsetYPx = 0
  userData._labelTargetPx = HOVER_HIGHLIGHT_SCREEN_PX
  userData._measureDecoration = 'point-marker'
  userData._textCanvas = canvas
  userData._textTexture = texture
  userData._textMaterial = material
  userData._textBaseScale = new THREE.Vector3(1, 1, 1)

  return sprite
}
function createPointMarkerAlphaTexture(): THREE.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    const texture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
    texture.needsUpdate = true
    return texture
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width * 0.48, 0, Math.PI * 2)
  ctx.fill()

  const texture = new THREE.CanvasTexture(canvas)
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true
  return texture
}

function disposeGroup(group: THREE.Group): void {
  group.traverse((obj) => {
    if (obj instanceof THREE.Sprite) {
      disposeTextSprite(obj)
      return
    }

    const meshLike = obj as THREE.Mesh | THREE.Line | THREE.Points
    meshLike.geometry?.dispose()

    if (meshLike.material) {
      const materials = Array.isArray(meshLike.material) ? meshLike.material : [meshLike.material]
      for (const material of materials) {
        material.dispose()
      }
    }
  })

  while (group.children.length > 0) {
    group.remove(group.children[0])
  }
}

function averagePoint(points: THREE.Vector3[]): THREE.Vector3 {
  const center = new THREE.Vector3()
  for (const point of points) {
    center.add(point)
  }
  return points.length > 0 ? center.divideScalar(points.length) : center
}

/** 测量虚线样式；双后端共用同一组语义参数。 */
const MEASURE_LINE_STYLE: WideLineStyle = Object.freeze({
  color: MEASURE_LINE_COLOR,
  linewidth: MEASURE_LINE_WIDTH_PX,
  dashed: true,
  dashSize: MEASURE_LINE_DASH_PX,
  gapSize: MEASURE_LINE_GAP_PX,
  depthTest: false,
  depthWrite: false,
  transparent: true,
})

function createDashedLine(wideLines: WideLineFactory, points: THREE.Vector3[]): WideLineObject {
  const line = wideLines.createLine(points, MEASURE_LINE_STYLE)
  ;(line.userData as MeasureLineUserData)._measureDashReference = averagePoint(points)
  return line
}

function createPointMarkers(
  positions: THREE.Vector3[],
  alphaTexture: THREE.Texture,
  color = MEASURE_LINE_COLOR,
  size = 6
): THREE.Group {
  const group = new THREE.Group()
  ;(group.userData as AnchoredLabelUserData)._measureDecoration = 'point-marker'
  if (positions.length === 0) {
    return group
  }

  const outerGeometry = new THREE.BufferGeometry()
  outerGeometry.setFromPoints(positions)
  const outerMaterial = createPointMaterial(color, size)
  outerMaterial.alphaMap = alphaTexture
  outerMaterial.transparent = true
  outerMaterial.alphaTest = 0.5
  group.add(new THREE.Points(outerGeometry, outerMaterial))

  const innerGeometry = new THREE.BufferGeometry()
  innerGeometry.setFromPoints(positions)
  const innerMaterial = createPointMaterial(0xffffff, Math.max(size * 0.42, 2.5))
  innerMaterial.alphaMap = alphaTexture
  innerMaterial.transparent = true
  innerMaterial.alphaTest = 0.5
  group.add(new THREE.Points(innerGeometry, innerMaterial))

  return group
}

function addPointMarkersAndLabels(
  group: THREE.Group,
  positions: THREE.Vector3[],
  alphaTexture: THREE.Texture,
  pointLabels?: ReadonlyArray<string | undefined>
): void {
  if (positions.length === 0) {
    return
  }

  group.add(createPointMarkers(positions, alphaTexture, MEASURE_LINE_COLOR, POINT_MARKER_SIZE))
  for (let i = 0; i < positions.length; i += 1) {
    group.add(createPointLabel(i, positions[i], pointLabels?.[i]))
  }
}

// ========================================
// author:Felix
// task:角度测量填充颜色调整
// explain：将角度测量扇形填充统一调整为红色，保持线条和填充视觉一致
// ========================================

/**
 * 添加角度扇形填充和角度标签
 * 角度测量和坡度角展示时调用，用于渲染角度区域与角度数值
 */
function addAngleFillAndLabel(
  group: THREE.Group,
  vertex: THREE.Vector3,
  firstPoint: THREE.Vector3,
  secondPoint: THREE.Vector3,
  angleDegrees: number
): void {
  const d1 = firstPoint.distanceTo(vertex)
  const d2 = secondPoint.distanceTo(vertex)
  const fillRadius = Math.min(d1, d2) * 0.5
  const v1 = new THREE.Vector3().subVectors(firstPoint, vertex)
  const v2 = new THREE.Vector3().subVectors(secondPoint, vertex)

  if (v1.lengthSq() > 1e-10 && v2.lengthSq() > 1e-10 && fillRadius > 0) {
    v1.normalize()
    v2.normalize()

    const normal = new THREE.Vector3().crossVectors(v1, v2)
    if (normal.lengthSq() > 1e-10) {
      normal.normalize()

      const tangent = new THREE.Vector3().crossVectors(normal, v1).normalize()
      const angle = Math.acos(THREE.MathUtils.clamp(v1.dot(v2), -1, 1))
      const segments = Math.max(8, Math.ceil(angle / (Math.PI / 32)))
      const fillPositions: number[] = []

      for (let i = 0; i < segments; i += 1) {
        const t0 = (i / segments) * angle
        const t1 = ((i + 1) / segments) * angle
        const arcPoint0 = vertex.clone().add(
          v1
            .clone()
            .multiplyScalar(Math.cos(t0) * fillRadius)
            .add(tangent.clone().multiplyScalar(Math.sin(t0) * fillRadius))
        )
        const arcPoint1 = vertex.clone().add(
          v1
            .clone()
            .multiplyScalar(Math.cos(t1) * fillRadius)
            .add(tangent.clone().multiplyScalar(Math.sin(t1) * fillRadius))
        )

        fillPositions.push(
          vertex.x,
          vertex.y,
          vertex.z,
          arcPoint0.x,
          arcPoint0.y,
          arcPoint0.z,
          arcPoint1.x,
          arcPoint1.y,
          arcPoint1.z
        )
      }

      const fillGeometry = new THREE.BufferGeometry()
      fillGeometry.setAttribute('position', new THREE.Float32BufferAttribute(fillPositions, 3))
      fillGeometry.computeVertexNormals()
      group.add(new THREE.Mesh(fillGeometry, createFillMaterial(MEASURE_LINE_COLOR)))
    }
  }

  const labelDirection = midpoint(firstPoint, secondPoint).sub(vertex)
  const label = createMeasureLabel(formatAngle(angleDegrees))
  if (labelDirection.lengthSq() > 1e-10 && fillRadius > 0) {
    label.position.copy(vertex).addScaledVector(labelDirection.normalize(), fillRadius * 0.5)
  } else if (v1.lengthSq() > 1e-10) {
    label.position.copy(vertex).addScaledVector(v1.normalize(), Math.max(d1 * 0.25, 0.2))
  } else if (v2.lengthSq() > 1e-10) {
    label.position.copy(vertex).addScaledVector(v2.normalize(), Math.max(d2 * 0.25, 0.2))
  } else {
    label.position.copy(vertex)
  }
  group.add(label)
}

function stripPointDecorations(group: THREE.Group): void {
  const removable = group.children.filter((child) => {
    const decoration = (child.userData as AnchoredLabelUserData)._measureDecoration
    return decoration === 'point-marker' || decoration === 'point-label'
  })

  for (const child of removable) {
    group.remove(child)
    disposeGroup(child as THREE.Group)
  }
}

function createPreviewMeasureVisual(
  wideLines: WideLineFactory,
  points: ReadonlyArray<MeasurePoint>,
  measureType: MeasureType,
  alphaTexture: THREE.Texture,
  displayPoints: ReadonlyArray<MeasurePoint>,
  preference: UnitPreference,
  enablePointRemoval: boolean,
  areaPlaneConstraintEnabled: boolean
): THREE.Group | null {
  if (points.length === 0) {
    return null
  }

  let areaBoundaryIndices: number[] | undefined
  if (measureType === 'area' && points.length >= 3) {
    const computedBoundaryIndices = computeAreaBoundaryIndices(points, areaPlaneConstraintEnabled)
    if (!computedBoundaryIndices) {
      return null
    }
    areaBoundaryIndices = computedBoundaryIndices
  }

  const result = buildMeasureResult(points, measureType, {
    areaBoundaryIndices,
    areaPlaneConstraintEnabled,
  })
  const visual =
    measureType === 'coordinate'
      ? buildCoordinateVisual(
          result,
          alphaTexture,
          preference,
          buildMeasureResult(displayPoints, measureType),
          'draft',
          enablePointRemoval
        )
      : createResultVisual(wideLines, result, alphaTexture, preference)
  stripPointDecorations(visual)

  if (visual.children.length === 0) {
    disposeGroup(visual)
    return null
  }

  return visual
}

function buildCoordinateVisual(
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference,
  displayResult: MeasureResult = result,
  removalScope: 'draft' | 'result' = 'result',
  enablePointRemoval = false
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-coordinate-${result.id}`

  const positions = result.points.map((point) => point.position)
  if (positions.length === 0) {
    return group
  }

  addPointMarkersAndLabels(
    group,
    positions,
    alphaTexture,
    result.points.map((point) => point.label)
  )

  for (let i = 0; i < positions.length; i += 1) {
    const displayPosition = displayResult.points[i]?.position
    if (!displayPosition) {
      continue
    }

    const point = result.points[i]
    const removeTarget =
      enablePointRemoval && point.id
        ? removalScope === 'draft'
          ? ({ scope: 'draft', pointId: point.id } as const)
          : ({ scope: 'result', resultId: result.id, pointId: point.id } as const)
        : undefined
    const label = createAnchoredLabel(
      formatCoordinateLabel(displayPosition, preference),
      positions[i],
      removeTarget
        ? { ...COORDINATE_LABEL_OPTIONS, topRightAction: REMOVE_ACTION_OPTIONS }
        : COORDINATE_LABEL_OPTIONS,
      COORDINATE_LABEL_OFFSET_PX,
      COORDINATE_LABEL_SCREEN_PX
    )
    if (removeTarget) {
      ;(label.userData as AnchoredLabelUserData)._measurePointRemoveTarget = removeTarget
    }
    group.add(label)
  }

  return group
}

function buildDistanceVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-distance-${result.id}`

  const positions = result.points.map((point) => point.position)
  if (positions.length < 2) {
    return group
  }

  group.add(createDashedLine(wideLines, positions))
  addPointMarkersAndLabels(group, positions, alphaTexture)

  for (let i = 0; i < result.segmentDistances.length; i++) {
    const label = createMeasureLabel(formatDistance(result.segmentDistances[i], preference))
    label.position.copy(midpoint(positions[i], positions[i + 1]))
    group.add(label)
  }

  return group
}

function buildHeightVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-height-${result.id}`

  const positions = result.points.map((point) => point.position)
  if (positions.length < 2 || !result.height) {
    return group
  }

  const p0 = positions[0]
  const p1 = positions[positions.length - 1]
  const lowPoint = p0.z <= p1.z ? p0 : p1
  const highPoint = lowPoint === p0 ? p1 : p0
  const bottomCorner = new THREE.Vector3(highPoint.x, highPoint.y, lowPoint.z)

  group.add(createDashedLine(wideLines, [p0, p1]))
  group.add(createDashedLine(wideLines, [lowPoint, bottomCorner]))
  group.add(createDashedLine(wideLines, [bottomCorner, highPoint]))
  addPointMarkersAndLabels(group, [p0, p1], alphaTexture)

  const verticalLabel = createMeasureLabel(formatDistance(result.height.vertical, preference))
  verticalLabel.position.copy(midpoint(bottomCorner, highPoint))
  group.add(verticalLabel)

  const horizontalLabel = createMeasureLabel(formatDistance(result.height.horizontal, preference))
  horizontalLabel.position.copy(midpoint(lowPoint, bottomCorner))
  group.add(horizontalLabel)

  const directLabel = createMeasureLabel(formatDistance(result.distance, preference))
  const directLabelOffset = new THREE.Vector3(p1.y - p0.y, -(p1.x - p0.x), 0)
  if (directLabelOffset.lengthSq() <= 1e-8) {
    directLabelOffset.set(1, 0, 0)
  }
  directLabel.position.copy(midpoint(p0, p1)).addScaledVector(directLabelOffset.normalize(), 0.2)
  group.add(directLabel)

  addAngleFillAndLabel(group, lowPoint, bottomCorner, highPoint, result.height.slopeAngle)

  return group
}

function buildCircleVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-circle-${result.id}`

  if (!result.circle) {
    return group
  }

  const { center, radius } = result.circle
  const positions = result.points.map((point) => point.position)

  const normal = new THREE.Vector3()
  if (positions.length >= 3) {
    const ab = new THREE.Vector3().subVectors(positions[1], positions[0])
    const ac = new THREE.Vector3().subVectors(positions[2], positions[0])
    normal.crossVectors(ab, ac).normalize()
  }

  if (normal.lengthSq() < 1e-6) {
    normal.set(0, 1, 0)
  }

  const up = normal.clone()
  const right = new THREE.Vector3()
  if (Math.abs(up.dot(new THREE.Vector3(0, 1, 0))) > 0.99) {
    right.crossVectors(up, new THREE.Vector3(1, 0, 0)).normalize()
  } else {
    right.crossVectors(up, new THREE.Vector3(0, 1, 0)).normalize()
  }
  const forward = new THREE.Vector3().crossVectors(right, up).normalize()

  const circlePoints: THREE.Vector3[] = []
  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const theta = (2 * Math.PI * i) / CIRCLE_SEGMENTS
    circlePoints.push(
      center
        .clone()
        .addScaledVector(right, Math.cos(theta) * radius)
        .addScaledVector(forward, Math.sin(theta) * radius)
    )
  }

  group.add(createDashedLine(wideLines, circlePoints))

  const centerMarker = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 0.03, 8, 8),
    new THREE.MeshBasicMaterial({
      color: MEASURE_LINE_COLOR,
      depthTest: false,
      depthWrite: false,
    })
  )
  centerMarker.position.copy(center)
  group.add(centerMarker)
  addPointMarkersAndLabels(group, positions, alphaTexture)

  const radiusLabel = createMeasureLabel(formatDistance(radius, preference))
  radiusLabel.position.copy(midpoint(center, positions[0]))
  group.add(radiusLabel)

  return group
}

function buildAzimuthVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-azimuth-${result.id}`

  const positions = result.points.map((point) => point.position)
  if (positions.length < 2 || result.azimuth === undefined) {
    return group
  }

  const p0 = positions[0]
  const p1 = positions[1]
  const distance = p0.distanceTo(p1)

  group.add(createDashedLine(wideLines, [p0, p1]))

  const northEnd = new THREE.Vector3(p0.x, p0.y + distance * 0.5, p0.z)
  group.add(createDashedLine(wideLines, [p0, northEnd]))

  const arcRadius = distance * 0.3
  const arcPoints: THREE.Vector3[] = []
  for (let i = 0; i <= ARC_SEGMENTS; i++) {
    const t = result.azimuth * (i / ARC_SEGMENTS)
    arcPoints.push(
      new THREE.Vector3(p0.x + Math.sin(t) * arcRadius, p0.y + Math.cos(t) * arcRadius, p0.z)
    )
  }
  group.add(createDashedLine(wideLines, arcPoints))
  addPointMarkersAndLabels(group, positions, alphaTexture)

  const angleDeg = THREE.MathUtils.radToDeg(result.azimuth)
  const normalizedDeg = ((angleDeg % 360) + 360) % 360
  const label = createMeasureLabel(formatAngle(normalizedDeg))
  const midAngle = result.azimuth * 0.5
  label.position.set(
    p0.x + Math.sin(midAngle) * arcRadius * 1.3,
    p0.y + Math.cos(midAngle) * arcRadius * 1.3,
    p0.z
  )
  group.add(label)

  return group
}

function buildAngleVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-angle-${result.id}`

  const positions = result.points.map((point) => point.position)
  if (positions.length !== 3 || result.angle === null) {
    return group
  }

  const [p0, p1, p2] = positions
  group.add(createDashedLine(wideLines, [p0, p1]))
  group.add(createDashedLine(wideLines, [p2, p1]))

  addPointMarkersAndLabels(group, positions, alphaTexture)
  addAngleFillAndLabel(group, p1, p0, p2, result.angle)

  return group
}

function buildAreaVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference
): THREE.Group {
  const group = new THREE.Group()
  group.name = `measure-area-${result.id}`

  const positions = result.points.map((point) => point.position)
  const boundaryPositions = resolveAreaBoundaryPoints(
    result.points,
    result.areaBoundaryIndices
  ).map((point) => point.position)
  if (boundaryPositions.length < 3 || result.area === null) {
    return group
  }

  const closedPoints = [...boundaryPositions, boundaryPositions[0]]
  group.add(createDashedLine(wideLines, closedPoints))

  let projected: THREE.Vector2[]
  let fillPositions: ReadonlyArray<THREE.Vector3> = boundaryPositions
  if (result.areaPlaneConstraintEnabled === false) {
    const projection = computeAreaPlaneProjection(
      boundaryPositions.map((position) => ({ position }))
    )
    if (!projection) {
      return group
    }
    projected = [...projection.projectedPoints]
    fillPositions = projection.planePoints
  } else {
    const normal = new THREE.Vector3()
    const ab = new THREE.Vector3().subVectors(boundaryPositions[1], boundaryPositions[0])
    const ac = new THREE.Vector3().subVectors(boundaryPositions[2], boundaryPositions[0])
    normal.crossVectors(ab, ac).normalize()
    if (normal.lengthSq() < 1e-6) {
      normal.set(0, 1, 0)
    }

    const right = new THREE.Vector3()
    if (Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.99) {
      right.crossVectors(normal, new THREE.Vector3(1, 0, 0)).normalize()
    } else {
      right.crossVectors(normal, new THREE.Vector3(0, 1, 0)).normalize()
    }
    const forward = new THREE.Vector3().crossVectors(right, normal).normalize()
    projected = boundaryPositions.map((point) => {
      const delta = new THREE.Vector3().subVectors(point, boundaryPositions[0])
      return new THREE.Vector2(delta.dot(right), delta.dot(forward))
    })
  }

  const triangles = THREE.ShapeUtils.triangulateShape(projected, [])
  const fillGeometry = new THREE.BufferGeometry()
  const vertices = new Float32Array(fillPositions.length * 3)
  for (let i = 0; i < fillPositions.length; i++) {
    vertices[i * 3] = fillPositions[i].x
    vertices[i * 3 + 1] = fillPositions[i].y
    vertices[i * 3 + 2] = fillPositions[i].z
  }
  fillGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3))

  const indices: number[] = []
  for (const triangle of triangles) {
    indices.push(triangle[0], triangle[1], triangle[2])
  }
  fillGeometry.setIndex(indices)
  group.add(
    new THREE.Mesh(
      fillGeometry,
      createFillMaterial(MEASURE_AREA_FILL_COLOR, MEASURE_AREA_FILL_OPACITY)
    )
  )
  addPointMarkersAndLabels(group, positions, alphaTexture)

  const centroid = new THREE.Vector3()
  for (const point of boundaryPositions) {
    centroid.add(point)
  }
  centroid.divideScalar(boundaryPositions.length)

  const label = createMeasureLabel(formatArea(result.area, preference).displayText)
  label.position.copy(centroid)
  group.add(label)

  return group
}

function createResultVisual(
  wideLines: WideLineFactory,
  result: MeasureResult,
  alphaTexture: THREE.Texture,
  preference: UnitPreference,
  enablePointRemoval = false
): THREE.Group {
  const origin = resolveMeasureVisualOrigin(result.points)
  const localResult = createLocalMeasureResult(result, origin)
  const visual = (() => {
    switch (localResult.measureType) {
      case 'coordinate':
        return buildCoordinateVisual(
          localResult,
          alphaTexture,
          preference,
          result,
          'result',
          enablePointRemoval
        )
      case 'distance':
        return buildDistanceVisual(wideLines, localResult, alphaTexture, preference)
      case 'height':
        return buildHeightVisual(wideLines, localResult, alphaTexture, preference)
      case 'circle':
        return buildCircleVisual(wideLines, localResult, alphaTexture, preference)
      case 'azimuth':
        return buildAzimuthVisual(wideLines, localResult, alphaTexture)
      case 'angle':
        return buildAngleVisual(wideLines, localResult, alphaTexture)
      case 'area':
        return buildAreaVisual(wideLines, localResult, alphaTexture, preference)
      default:
        return buildDistanceVisual(wideLines, localResult, alphaTexture, preference)
    }
  })()

  visual.position.copy(origin)
  return visual
}

/**
 * 测量覆盖渲染插件，将测量结果与预览可视化到 Overlay3D 阶段。
 */
export class MeasureOverlayPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'measure-overlay'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 55
  /** 依赖的服务名列表 */
  readonly dependencies = ['MeasureService', 'RenderPipeline', 'ViewEventRouter'] as const

  /** 渲染贡献者类型标识 */
  readonly kind = 'measure-overlay'
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  private context!: EngineContext
  private measureService!: MeasureService
  private pipeline!: RenderPipeline
  private unitPresentation!: UnitPresentationService
  private viewEventRouter!: ViewEventRouter
  private unsubscribeUnitPreference: (() => void) | null = null
  private viewRegistry: ViewRegistry | null = null
  private wideLines: WideLineFactory | null = null

  private readonly root = new THREE.Group()
  private readonly overlayScene = new THREE.Scene()
  private readonly visualCache = new Map<string, THREE.Group>()
  private readonly visualResults = new Map<string, MeasureResult>()
  private readonly completedPointLabelMaxByResultId = new Map<string, number>()
  private readonly previewGroup = new THREE.Group()
  private readonly cameraDirection = new THREE.Vector3()
  private readonly cameraDepthOffset = new THREE.Vector3()
  private readonly pointMarkerAlphaTexture = createPointMarkerAlphaTexture()
  private completedMaxPointLabelNumber = 0
  private readonly removeHitTargetsByView = new Map<string, RemoveHitTarget[]>()
  private pressedRemoveTarget: CoordinatePointRemoveTarget | null = null
  private recentRemoveActivation: RecentRemoveActivation | null = null

  private readonly inputConsumer: ViewInputConsumer = {
    name: 'measure-overlay-remove',
    priority: 110,
    onPointerDown: (event) => this.handleRemovePointerDown(event),
    onPointerUp: (event) => this.handleRemovePointerUp(event),
    onDoubleClick: (event) => this.handleRemoveDoubleClick(event),
  }

  constructor() {
    this.root.name = 'measureOverlayRoot'
    this.previewGroup.name = 'measurePreview'
    this.root.add(this.previewGroup)
  }

  /**
   * 预加载与 backend 匹配的宽线实现。
   *
   * WebGPU 的 `Line2NodeMaterial` 来自 `three/webgpu`，必须按需动态加载，避免 WebGL 宿主
   * 为了测量线宽把整个 WebGPU 构建拖进产物。
   *
   * @param context - 引擎上下文
   */
  async preload(context: EngineContext): Promise<void> {
    this.wideLines = await createWideLineFactory(context.graphics.kind)
  }

  /** 预加载失败时释放宽线工厂，允许重新初始化。 */
  rollbackPreload(): void {
    this.wideLines = null
  }

  /**
   * 初始化插件，缓存 EngineContext。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 取当前 backend 的宽线工厂；未经过 preload（例如测试）时按 WebGL2 语义兜底。 */
  private resolveWideLines(): WideLineFactory {
    if (!this.wideLines) {
      this.wideLines = createWebGLWideLineFactory()
    }
    return this.wideLines
  }

  /** 启动插件，解析依赖并挂载 overlay 根节点到场景 */
  onStart(): void {
    this.measureService = this.context.getService('MeasureService')
    this.pipeline = this.context.getService('RenderPipeline')
    this.viewEventRouter = this.context.getService('ViewEventRouter')
    this.unitPresentation = this.context.getService('UnitPresentationService')
    this.unsubscribeUnitPreference = this.unitPresentation.subscribe(() => {
      this.clearResultVisuals()
      this.context.renderInvalidation.invalidate('measure.unitPreference.changed')
    })
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.pipeline.addRenderContributor(this)
    this.viewEventRouter.addConsumer(this.inputConsumer)
    this.resolveToolsRoot(getActiveViewId(this.viewRegistry)).add(this.root)
  }

  /** 销毁插件，释放可视化资源并移除 overlay */
  onDestroy(): void {
    this.pipeline.removeRenderContributor(this)
    this.viewEventRouter.removeConsumer(this.inputConsumer)
    this.unsubscribeUnitPreference?.()
    this.unsubscribeUnitPreference = null
    this.clearResultVisuals()
    disposeGroup(this.previewGroup)
    this.pointMarkerAlphaTexture.dispose()
    this.removeHitTargetsByView.clear()
    this.pressedRemoveTarget = null
    this.recentRemoveActivation = null

    if (this.root.parent) {
      this.root.parent.remove(this.root)
    }
    this.viewRegistry = null
  }

  /**
   * 渲染测量覆盖层。
   *
   * @param frame - 视图帧上下文
   * @param _phase - 当前渲染阶段，已由 phases 约束
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    const toolsRoot = this.resolveToolsRoot(ctx.viewId)
    if (this.root.parent !== toolsRoot) {
      this.root.parent?.remove(this.root)
      toolsRoot.add(this.root)
    }

    this.syncResultVisuals(ctx.viewId)
    this.syncPreview(ctx.viewId)

    const hasFinishedResults = Array.from(this.visualCache.values()).some((group) => group.visible)
    const hasPreview = this.previewGroup.children.length > 0
    if (!hasFinishedResults && !hasPreview) {
      this.removeHitTargetsByView.delete(ctx.viewId)
      return
    }

    // 放大镜等离屏 pass 不重复绘制测量覆盖层（跨 backend 判断，不读 legacy outputTarget）。
    if (ctx.rendersToOffscreenTarget) {
      return
    }

    this.root.updateMatrixWorld(true)
    this.updateOverlayObjects(
      frame.camera,
      frame.viewport.width,
      frame.viewport.height,
      ctx.viewport.width,
      ctx.viewport.height
    )
    this.root.updateMatrixWorld(true)
    this.syncRemoveHitTargets(ctx.viewId, frame.camera, frame.viewport.width, frame.viewport.height)

    if (this.root.parent) {
      this.root.parent.remove(this.root)
    }
    this.overlayScene.add(this.root)

    // 提交统一走 renderScene：viewport/scissor 与 output target 由 backend 依当前 View 决定；
    // overlay 只保留「测量线需要看到全部 layer」这一领域语义。
    try {
      submitSceneInView(ctx, this.overlayScene, frame.camera, { layerMask: 0xffffffff })
    } finally {
      this.overlayScene.remove(this.root)
      toolsRoot.add(this.root)
    }
  }

  private resolveToolsRoot(viewId: string | null): THREE.Object3D {
    return getViewToolsRoot(this.context, this.viewRegistry, viewId)
  }

  private resolveUnitPreference(result?: MeasureResult): UnitPreference {
    return (
      this.measureService.resolveUnitPreference?.(this.unitPresentation.preference, result) ??
      this.unitPresentation.preference
    )
  }

  private syncResultVisuals(viewId: string): void {
    const results = this.measureService.getResults()
    const currentIds = new Set<string>()
    let completedPointLabelCacheChanged = false

    for (const result of results) {
      currentIds.add(result.id)

      const cachedResult = this.visualResults.get(result.id)
      if (cachedResult && cachedResult !== result) {
        const cachedVisual = this.visualCache.get(result.id)
        if (cachedVisual) {
          disposeGroup(cachedVisual)
          this.root.remove(cachedVisual)
        }
        this.visualCache.delete(result.id)
        this.visualResults.delete(result.id)
        this.completedPointLabelMaxByResultId.delete(result.id)
        completedPointLabelCacheChanged = true
      }

      if (!this.visualCache.has(result.id)) {
        const visual = createResultVisual(
          this.resolveWideLines(),
          result,
          this.pointMarkerAlphaTexture,
          this.resolveUnitPreference(result),
          typeof this.measureService.removeResultPoint === 'function'
        )
        this.visualCache.set(result.id, visual)
        this.visualResults.set(result.id, result)
        this.completedPointLabelMaxByResultId.set(
          result.id,
          result.measureType === 'coordinate' ? getMaxPointLabelNumber(result.points) : 0
        )
        completedPointLabelCacheChanged = true
        this.root.add(visual)
      }

      const visible = this.measureService.getResultVisible?.(result.id) ?? true
      const group = this.visualCache.get(result.id)
      if (group) {
        group.visible = visible && this.shouldRenderResultInView(result, viewId)
      }
    }

    for (const [id, group] of this.visualCache) {
      if (!currentIds.has(id)) {
        disposeGroup(group)
        this.root.remove(group)
        this.visualCache.delete(id)
        this.visualResults.delete(id)
        this.completedPointLabelMaxByResultId.delete(id)
        completedPointLabelCacheChanged = true
      }
    }

    if (completedPointLabelCacheChanged) {
      this.refreshCompletedMaxPointLabelNumber()
    }
  }

  private clearResultVisuals(): void {
    for (const [, group] of this.visualCache) {
      disposeGroup(group)
      this.root.remove(group)
    }
    this.visualCache.clear()
    this.visualResults.clear()
    this.completedPointLabelMaxByResultId.clear()
    this.completedMaxPointLabelNumber = 0
  }

  private refreshCompletedMaxPointLabelNumber(): void {
    let maxPointLabelNumber = 0
    for (const pointLabelNumber of this.completedPointLabelMaxByResultId.values()) {
      maxPointLabelNumber = Math.max(maxPointLabelNumber, pointLabelNumber)
    }
    this.completedMaxPointLabelNumber = maxPointLabelNumber
  }

  private handleRemovePointerDown(event: ViewPointerEvent): boolean {
    this.pressedRemoveTarget = null
    if (event.button !== 0) {
      return false
    }
    const hit = this.findRemoveHitTarget(event)
    if (!hit) {
      return false
    }
    this.pressedRemoveTarget = hit.target
    return true
  }

  private handleRemovePointerUp(event: ViewPointerEvent): boolean {
    const pressedTarget = this.pressedRemoveTarget
    if (!pressedTarget) {
      return false
    }
    this.pressedRemoveTarget = null
    if (event.domEvent.type === 'pointercancel') {
      return true
    }

    const hit = this.findRemoveHitTarget(event)
    if (!hit || this.getRemoveTargetKey(hit.target) !== this.getRemoveTargetKey(pressedTarget)) {
      return true
    }

    const removed =
      pressedTarget.scope === 'draft'
        ? this.measureService.removeDraftPoint?.(pressedTarget.pointId)
        : this.measureService.removeResultPoint?.(pressedTarget.resultId, pressedTarget.pointId)
    if (removed) {
      this.recentRemoveActivation = {
        viewId: event.viewId,
        rect: { ...hit.rect },
        timeStamp: event.domEvent.timeStamp,
      }
    }
    return true
  }

  private handleRemoveDoubleClick(event: ViewPointerEvent): boolean {
    if (this.findRemoveHitTarget(event)) {
      return true
    }

    const recentActivation = this.recentRemoveActivation
    this.recentRemoveActivation = null
    if (!recentActivation || recentActivation.viewId !== event.viewId) {
      return false
    }

    const elapsed = event.domEvent.timeStamp - recentActivation.timeStamp
    if (elapsed < 0 || elapsed > REMOVE_DOUBLE_CLICK_GUARD_MS) {
      return false
    }

    const localX = event.screen.x - event.viewRect.x
    const localY = event.screen.y - event.viewRect.y
    const { rect } = recentActivation
    return (
      localX >= rect.x &&
      localX <= rect.x + rect.width &&
      localY >= rect.y &&
      localY <= rect.y + rect.height
    )
  }

  private findRemoveHitTarget(event: ViewPointerEvent): RemoveHitTarget | undefined {
    const localX = event.screen.x - event.viewRect.x
    const localY = event.screen.y - event.viewRect.y
    const targets = this.removeHitTargetsByView.get(event.viewId) ?? []
    return [...targets].reverse().find(({ rect }) => {
      return (
        localX >= rect.x &&
        localX <= rect.x + rect.width &&
        localY >= rect.y &&
        localY <= rect.y + rect.height
      )
    })
  }

  private getRemoveTargetKey(target: CoordinatePointRemoveTarget): string {
    return target.scope === 'draft'
      ? `draft:${target.pointId}`
      : `result:${target.resultId}:${target.pointId}`
  }

  private syncRemoveHitTargets(
    viewId: string,
    camera: THREE.Camera,
    viewportWidth: number,
    viewportHeight: number
  ): void {
    const targets: RemoveHitTarget[] = []
    if (viewportWidth <= 0 || viewportHeight <= 0) {
      this.removeHitTargetsByView.set(viewId, targets)
      return
    }

    this.root.traverseVisible((object) => {
      if (!(object instanceof THREE.Sprite)) {
        return
      }
      const userData = object.userData as AnchoredLabelUserData
      const target = userData._measurePointRemoveTarget
      const metrics = getTextSpriteMetrics(object)
      const actionBounds = metrics?.actionBounds
      if (!target || !metrics || !actionBounds) {
        return
      }

      const ndc = object.getWorldPosition(new THREE.Vector3()).project(camera)
      if (ndc.z < -1 || ndc.z > 1) {
        return
      }
      const screenX = ((ndc.x + 1) * viewportWidth) / 2
      const screenY = ((-ndc.y + 1) * viewportHeight) / 2
      const targetPx = userData._labelTargetPx ?? COORDINATE_LABEL_SCREEN_PX
      const labelWidth = metrics.cssWidth * targetPx * 0.01
      const labelHeight = metrics.cssHeight * targetPx * 0.01
      const cssToScreenX = labelWidth / metrics.textureCssWidth
      const cssToScreenY = labelHeight / metrics.textureCssHeight
      const labelLeft = screenX - object.center.x * labelWidth
      const labelTop = screenY - (1 - object.center.y) * labelHeight
      const padding = REMOVE_ACTION_HIT_PADDING_PX
      targets.push({
        target,
        rect: {
          x: labelLeft + actionBounds.x * cssToScreenX - padding,
          y: labelTop + actionBounds.y * cssToScreenY - padding,
          width: actionBounds.width * cssToScreenX + padding * 2,
          height: actionBounds.height * cssToScreenY + padding * 2,
        },
      })
    })
    this.removeHitTargetsByView.set(viewId, targets)
  }

  private shouldRenderResultInView(result: MeasureResult, viewId: string): boolean {
    return result.viewId == null || result.viewId === viewId
  }

  private updateOverlayObjects(
    camera: THREE.Camera,
    labelViewportWidth: number,
    labelViewportHeight: number,
    lineViewportWidth: number,
    lineViewportHeight: number
  ): void {
    const wideLines = this.resolveWideLines()
    this.root.traverse((obj) => {
      if (obj instanceof THREE.Sprite && labelViewportWidth > 0 && labelViewportHeight > 0) {
        const anchor = (obj.userData as AnchoredLabelUserData)._labelAnchor
        if (anchor) {
          const offsetYPx = (obj.userData as AnchoredLabelUserData)._labelOffsetYPx ?? 0

          this.positionLabelWithScreenOffset(
            obj,
            anchor,
            camera,
            labelViewportWidth,
            labelViewportHeight,
            offsetYPx
          )
        }

        const targetPx =
          (obj.userData as AnchoredLabelUserData)._labelTargetPx ?? COORDINATE_LABEL_SCREEN_PX
        updateTextSpriteScaleForCamera(obj, camera, labelViewportHeight, targetPx)
      }

      const material = this.getLineMaterial(obj)
      if (!material) {
        return
      }
      wideLines.applyViewportResolution(material, lineViewportWidth, lineViewportHeight)
      const reference = (obj.userData as MeasureLineUserData)._measureDashReference
      if (!reference) {
        return
      }
      const referenceWorld = reference.clone().applyMatrix4(obj.matrixWorld)
      const pixelsPerWorld = this.computePixelsPerWorld(referenceWorld, camera, lineViewportHeight)
      if (pixelsPerWorld > 0) {
        wideLines.applyDashScale(material, pixelsPerWorld)
      }
    })
  }

  private positionLabelWithScreenOffset(
    label: THREE.Sprite,
    anchor: THREE.Vector3,
    camera: THREE.Camera,
    viewportWidth: number,
    viewportHeight: number,
    offsetYPx: number
  ): void {
    const anchorWorld = anchor.clone()
    if (label.parent) {
      anchorWorld.applyMatrix4(label.parent.matrixWorld)
    }

    const screenPos = anchorWorld.clone().project(camera)
    screenPos.x = ((screenPos.x + 1) * viewportWidth) / 2
    screenPos.y = ((-screenPos.y + 1) * viewportHeight) / 2
    screenPos.z = 0
    screenPos.y += offsetYPx

    const labelPos = new THREE.Vector3(
      (screenPos.x / viewportWidth) * 2 - 1,
      -(screenPos.y / viewportHeight) * 2 + 1,
      0.5
    ).unproject(camera)

    if (camera instanceof THREE.PerspectiveCamera) {
      const distance = camera.position.distanceTo(anchorWorld)
      const direction = labelPos.sub(camera.position).normalize()
      const labelWorldPosition = camera.position.clone().addScaledVector(direction, distance)
      label.position.copy(
        label.parent ? label.parent.worldToLocal(labelWorldPosition) : labelWorldPosition
      )
      return
    }

    label.position.copy(label.parent ? label.parent.worldToLocal(labelPos) : labelPos)
  }

  private getLineMaterial(obj: THREE.Object3D): WideLineMaterial | null {
    if (!('material' in obj)) {
      return null
    }
    return this.resolveWideLines().isWideLineMaterial(obj.material) ? obj.material : null
  }

  private computePixelsPerWorld(
    position: THREE.Vector3,
    camera: THREE.Camera,
    viewportHeight: number
  ): number {
    if (viewportHeight <= 0) {
      return 1
    }

    if (camera instanceof THREE.PerspectiveCamera) {
      camera.getWorldDirection(this.cameraDirection)
      const distance = this.cameraDepthOffset
        .subVectors(position, camera.position)
        .dot(this.cameraDirection)
      if (distance <= 0) {
        return 1
      }
      const fovRad = THREE.MathUtils.degToRad(camera.fov)
      const worldPerPixel = (2 * distance * Math.tan(fovRad / 2)) / viewportHeight
      return worldPerPixel > 0 ? 1 / worldPerPixel : 1
    }

    if (camera instanceof THREE.OrthographicCamera) {
      const frustumHeight = (camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6)
      const worldPerPixel = frustumHeight / viewportHeight
      return worldPerPixel > 0 ? 1 / worldPerPixel : 1
    }

    return 1
  }

  /**
   * 同步测量草稿预览渲染
   * 根据草稿视口或首点前预览视口过滤 overlay，避免多视口下预览内容串到其他视口
   */
  private syncPreview(viewId: string): void {
    disposeGroup(this.previewGroup)

    const previewService = this.measureService as PreviewMeasureService
    const draftViewId = previewService.getDraftViewId?.() ?? null
    const previewViewId = previewService.getPreviewViewId?.() ?? null
    const effectiveViewId = draftViewId ?? previewViewId
    if (effectiveViewId && effectiveViewId !== viewId) {
      this.previewGroup.position.set(0, 0, 0)
      return
    }

    const currentPoints = this.measureService.getCurrentPoints()
    const previewPoint = previewService.getPreviewPoint?.() ?? null
    const lastCurrentPoint = currentPoints[currentPoints.length - 1]?.position
    const effectivePreviewPoint =
      previewPoint && (!lastCurrentPoint || !lastCurrentPoint.equals(previewPoint.position))
        ? previewPoint
        : null
    const previewPositions = currentPoints.map((point) => point.position.clone())
    if (effectivePreviewPoint) {
      previewPositions.push(effectivePreviewPoint.position.clone())
    }
    const previewMeasurePoints: MeasurePoint[] = currentPoints.map((point) => ({
      ...point,
      position: point.position.clone(),
    }))
    if (effectivePreviewPoint) {
      previewMeasurePoints.push({ position: effectivePreviewPoint.position.clone() })
    }
    const measureType = this.measureService.getMeasureType()
    const isContinuousCoordinatePreview =
      measureType === 'coordinate' && this.measureService.getCoordinateMode() === 'continuous'
    const hostCoordinatePreviewLabel = isContinuousCoordinatePreview
      ? previewService.getCoordinatePreviewLabel?.()
      : undefined
    const previewPointLabel = isContinuousCoordinatePreview
      ? hostCoordinatePreviewLabel !== undefined
        ? (hostCoordinatePreviewLabel ?? undefined)
        : getNextPreviewPointLabel(currentPoints, this.completedMaxPointLabelNumber)
      : undefined
    const shouldRenderPreviewPointLabel =
      !isContinuousCoordinatePreview || hostCoordinatePreviewLabel !== null

    if (previewMeasurePoints.length === 0) {
      this.previewGroup.position.set(0, 0, 0)
      return
    }

    const previewOrigin = resolveMeasureVisualOrigin(previewMeasurePoints)
    const localCurrentPoints = currentPoints.map((point) => ({
      ...point,
      position: toLocalPosition(point.position, previewOrigin),
    }))
    const localEffectivePreviewPoint = effectivePreviewPoint
      ? { position: toLocalPosition(effectivePreviewPoint.position, previewOrigin) }
      : null
    const localPreviewPositions = previewPositions.map((position) =>
      toLocalPosition(position, previewOrigin)
    )
    const localPreviewMeasurePoints = previewMeasurePoints.map((point) => ({
      ...point,
      position: toLocalPosition(point.position, previewOrigin),
    }))
    this.previewGroup.position.copy(previewOrigin)

    if (currentPoints.length > 0) {
      addPointMarkersAndLabels(
        this.previewGroup,
        localCurrentPoints.map((point) => point.position.clone()),
        this.pointMarkerAlphaTexture,
        localCurrentPoints.map((point) => point.label)
      )
    }

    if (localEffectivePreviewPoint) {
      const hoverPosition = localEffectivePreviewPoint.position.clone()
      // 捕捉到点云或矢量图层时，都显示高亮方框。
      const hoverSnap = this.measureService.getHoverSnap?.()
      if (hoverSnap) {
        this.previewGroup.add(createHoverHighlightSprite(hoverPosition))
      }
      if (currentPoints.length === 0 || isContinuousCoordinatePreview) {
        this.previewGroup.add(
          createPointMarkers(
            [hoverPosition],
            this.pointMarkerAlphaTexture,
            MEASURE_LINE_COLOR,
            POINT_MARKER_SIZE
          )
        )
      }
      if (shouldRenderPreviewPointLabel) {
        this.previewGroup.add(
          createPointLabel(currentPoints.length, hoverPosition, previewPointLabel)
        )
      }
    }

    const previewVisual = createPreviewMeasureVisual(
      this.resolveWideLines(),
      localPreviewMeasurePoints,
      measureType,
      this.pointMarkerAlphaTexture,
      previewMeasurePoints,
      this.resolveUnitPreference(),
      typeof this.measureService.removeDraftPoint === 'function',
      this.measureService.isAreaPlaneConstraintEnabled?.() ?? true
    )
    if (previewVisual) {
      this.previewGroup.add(previewVisual)
      return
    }

    if (localPreviewPositions.length >= 2) {
      this.previewGroup.add(createDashedLine(this.resolveWideLines(), localPreviewPositions))
    }
  }
}

export default MeasureOverlayPlugin
